// libs
import { SWRResponse } from 'swr';
// App
import { useSWRAxiosGetRequest } from 'app-shared/lib/api/utils/swr-axios-request.hooks';
import useRSocket from 'app-shared/lib/api/utils/rsocket.hooks';
import { RSocketHookResponse } from 'app-shared/lib/api/utils/rsocket.hooks.interfaces';
import { Session } from 'app-shared/lib/api/user-access/auth';
import { ShopDateAvailabilities } from 'app-shared/lib/api/order-processing/shop/availabilities.interfaces';
import { Reservation } from '../reservation.interface';
import { RESERVATION_STATUS } from '../reservation-status.type';
import { ReservationSavedEvent } from '../reservation-saved-event.interface';
import {
  buildAvailabilitiesUrl,
  buildShopReservationsCountUrl,
  buildShopReservationsUrl,
} from './reservation-urls.utils';

const SECOND = 1000;
const MINUTE = SECOND * 60;
const REFETCH_RESERVATIONS_INTERVAL = 2 * MINUTE;

/**
 * Get the reservations of a shop over a period, ordered by start date
 */
export const useGetShopReservations = (
  shopId?: number,
  startDate?: Date,
  endDate?: Date,
  status?: RESERVATION_STATUS,
  shouldFetch = true
): SWRResponse<Reservation[], Error> => {
  const statusQueryStringPart = (status && `&status=${status}`) || '';
  const isReady = shouldFetch && Boolean(shopId) && Boolean(startDate) && Boolean(endDate);
  const url =
    isReady &&
    `${buildShopReservationsUrl(
      shopId
    )}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}${statusQueryStringPart}`;
  const urlOptional = url || null;

  return useSWRAxiosGetRequest(urlOptional, {
    revalidateOnFocus: false,
    refreshInterval: REFETCH_RESERVATIONS_INTERVAL,
  });
};

/**
 * Count the reservations of a shop over a period having one of the given statuses
 */
export const useCountShopReservations = (
  shopId: number | undefined,
  startDate: Date,
  endDate: Date,
  statuses: RESERVATION_STATUS[]
): SWRResponse<number, Error> => {
  const urlOptional = (shopId && buildShopReservationsCountUrl(shopId, startDate, endDate, statuses)) || null;

  return useSWRAxiosGetRequest(urlOptional, {
    revalidateOnFocus: false,
    refreshInterval: REFETCH_RESERVATIONS_INTERVAL,
  });
};

/**
 * Get the bookable slots of a shop place for a party, over {@code limit} days
 */
export const useGetReservationAvailabilities = (
  shopId?: number,
  googlePlaceId?: string,
  guestCount?: number,
  startDate?: Date,
  limit = 1
): SWRResponse<ShopDateAvailabilities[], Error> => {
  const isReady = Boolean(shopId) && Boolean(googlePlaceId) && guestCount != null && Boolean(startDate);
  const url = isReady && buildAvailabilitiesUrl(shopId, googlePlaceId, guestCount, startDate, limit);
  const urlOptional = url || null;

  return useSWRAxiosGetRequest(urlOptional, { revalidateOnFocus: false });
};

/**
 * Listens to shop reservations events
 */
export const useGetShopReservationsEvents = (
  shopId: number,
  session: Session,
  shouldListen = true
): RSocketHookResponse<ReservationSavedEvent> => {
  const messageMapping = (shouldListen && shopId && `shops.${shopId}.reservations.events`) || null;

  return useRSocket<ReservationSavedEvent>(messageMapping, session);
};
